/**
 * Phone normalization (mirror of server/helpers/phoneValidation.js normalizePhone)
 * Numbers saved without country prefix (e.g. Excel imports) get the building dial code.
 */

import { getBuildingCountryCode } from "@/utilities/countrySpecific";

/** Building country code -> international dial code and national number length */
const COUNTRY_PHONE_RULES = {
  TN: { dialCode: "216", localLength: 8 },
  KE: { dialCode: "254", localLength: 9 },
};

/**
 * Normalize a phone number to E.164 using the building's country as default.
 *   TN: "22123456" | "21622123456" | "0021622123456" | "22 123 456" -> "+21622123456"
 *   KE: "0712345678" | "712345678" | "254712345678" -> "+254712345678"
 * Anything that cannot be normalized is returned unchanged (trimmed) so validation still rejects it.
 * @param {string|number} phone
 * @param {string} countryCode - Optional, defaults to the active building country code
 * @returns {string}
 */
export function normalizePhone(phone, countryCode = getBuildingCountryCode()) {
  if (phone === null || phone === undefined) return phone;
  const raw = String(phone).trim();
  if (!raw) return raw;
  let s = raw.replace(/[\s.\-()/]/g, "");
  if (s.startsWith("00")) s = "+" + s.slice(2);
  if (/^\+\d+$/.test(s)) return s;
  const rules = COUNTRY_PHONE_RULES[countryCode];
  if (!rules || !/^\d+$/.test(s)) return raw;
  const { dialCode, localLength } = rules;
  if (s.length === localLength) return `+${dialCode}${s}`;
  if (s.length === localLength + 1 && s.startsWith("0"))
    return `+${dialCode}${s.slice(1)}`;
  if (s.length === dialCode.length + localLength && s.startsWith(dialCode))
    return `+${s}`;
  return raw;
}

/**
 * Normalize father / mother / guardian phones of a parent-like object in place.
 * @param {object} parent - object with optional father, mother, guardian
 * @returns {object} the same object
 */
export function normalizeParentPhones(parent) {
  if (!parent) return parent;
  ["father", "mother", "guardian"].forEach((type) => {
    if (parent[type] && parent[type].phone)
      parent[type].phone = normalizePhone(parent[type].phone);
  });
  return parent;
}

export default { normalizePhone, normalizeParentPhones };
